/**
 * PR75: Dependency Audit Risk Registry
 * Metadata-only. No network call. No DB. No provider. No live execution.
 * All entries and the registry object are deep-frozen for runtime immutability.
 * generatedInPr: 75
 */
// ---------------------------------------------------------------------------
// Deep freeze helper
// ---------------------------------------------------------------------------
function deepFreeze(obj) {
    if (obj === null || typeof obj !== "object")
        return obj;
    Object.getOwnPropertyNames(obj).forEach((name) => {
        const val = obj[name];
        if (val !== null && typeof val === "object") {
            deepFreeze(val);
        }
    });
    return Object.freeze(obj);
}
// ---------------------------------------------------------------------------
// Registry entries: DAR001–DAR020
// ---------------------------------------------------------------------------
export const SMART_SHIKKA_DEPENDENCY_AUDIT_ENTRIES = deepFreeze([
    {
        id: "DAR001",
        packageName: "lodash",
        affectedVersionRange: "<4.17.21",
        riskLevel: "high",
        riskCategory: "security_vulnerability",
        riskDescription: "Prototype pollution vulnerability in lodash versions before 4.17.21 (CVE-2021-23337).",
        remediation: "Upgrade lodash to >= 4.17.21.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2021-23337",
    },
    {
        id: "DAR002",
        packageName: "axios",
        affectedVersionRange: "<1.6.0",
        riskLevel: "high",
        riskCategory: "security_vulnerability",
        riskDescription: "SSRF vulnerability in axios versions before 1.6.0 (CVE-2023-45857).",
        remediation: "Upgrade axios to >= 1.6.0.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2023-45857",
    },
    {
        id: "DAR003",
        packageName: "semver",
        affectedVersionRange: "<7.5.2",
        riskLevel: "high",
        riskCategory: "security_vulnerability",
        riskDescription: "ReDoS vulnerability in semver versions before 7.5.2 (CVE-2022-25883).",
        remediation: "Upgrade semver to >= 7.5.2.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2022-25883",
    },
    {
        id: "DAR004",
        packageName: "minimatch",
        affectedVersionRange: "<3.1.2",
        riskLevel: "high",
        riskCategory: "security_vulnerability",
        riskDescription: "ReDoS vulnerability in minimatch versions before 3.1.2 (CVE-2022-3517).",
        remediation: "Upgrade minimatch to >= 3.1.2.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2022-3517",
    },
    {
        id: "DAR005",
        packageName: "node-fetch",
        affectedVersionRange: "<2.6.7",
        riskLevel: "high",
        riskCategory: "security_vulnerability",
        riskDescription: "Exposure of sensitive information in node-fetch before 2.6.7 (CVE-2022-0235).",
        remediation: "Upgrade node-fetch to >= 2.6.7 or >= 3.x.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2022-0235",
    },
    {
        id: "DAR006",
        packageName: "got",
        affectedVersionRange: "<11.8.5",
        riskLevel: "medium",
        riskCategory: "security_vulnerability",
        riskDescription: "Open redirect vulnerability in got before 11.8.5 (CVE-2022-33987).",
        remediation: "Upgrade got to >= 11.8.5 or >= 12.x.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2022-33987",
    },
    {
        id: "DAR007",
        packageName: "glob-parent",
        affectedVersionRange: "<5.1.2",
        riskLevel: "medium",
        riskCategory: "security_vulnerability",
        riskDescription: "ReDoS vulnerability in glob-parent before 5.1.2 (CVE-2020-28469).",
        remediation: "Upgrade glob-parent to >= 5.1.2.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2020-28469",
    },
    {
        id: "DAR008",
        packageName: "json5",
        affectedVersionRange: "<2.2.2",
        riskLevel: "high",
        riskCategory: "security_vulnerability",
        riskDescription: "Prototype pollution in json5 before 2.2.2 (CVE-2022-46175).",
        remediation: "Upgrade json5 to >= 2.2.2.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2022-46175",
    },
    {
        id: "DAR009",
        packageName: "moment",
        affectedVersionRange: "<2.29.4",
        riskLevel: "critical",
        riskCategory: "security_vulnerability",
        riskDescription: "Path traversal vulnerability in moment before 2.29.4 (CVE-2022-24785).",
        remediation: "Upgrade moment to >= 2.29.4 or migrate to date-fns/dayjs.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2022-24785",
    },
    {
        id: "DAR010",
        packageName: "moment",
        affectedVersionRange: ">=0.0.0",
        riskLevel: "medium",
        riskCategory: "abandoned",
        riskDescription: "Moment.js is in maintenance mode and no longer actively developed. New features are not being added.",
        remediation: "Migrate to date-fns or dayjs for new projects.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: null,
    },
    {
        id: "DAR011",
        packageName: "electron",
        affectedVersionRange: "<28.0.0",
        riskLevel: "high",
        riskCategory: "outdated_major",
        riskDescription: "Electron versions before 28.x are no longer receiving security updates.",
        remediation: "Upgrade electron to >= 28.0.0.",
        workspaceScope: "desktop",
        active: true,
        introducedInPr: 75,
        cveId: null,
    },
    {
        id: "DAR012",
        packageName: "typescript",
        affectedVersionRange: "<5.0.0",
        riskLevel: "medium",
        riskCategory: "outdated_major",
        riskDescription: "TypeScript versions before 5.x miss important type safety improvements and performance gains.",
        remediation: "Upgrade TypeScript to >= 5.0.0.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: null,
    },
    {
        id: "DAR013",
        packageName: "react",
        affectedVersionRange: "<18.0.0",
        riskLevel: "medium",
        riskCategory: "outdated_major",
        riskDescription: "React versions before 18.x lack concurrent rendering features and are approaching end of support.",
        remediation: "Upgrade React to >= 18.0.0.",
        workspaceScope: "web",
        active: true,
        introducedInPr: 75,
        cveId: null,
    },
    {
        id: "DAR014",
        packageName: "eslint",
        affectedVersionRange: "<8.0.0",
        riskLevel: "low",
        riskCategory: "outdated_major",
        riskDescription: "ESLint versions before 8.x miss flat config support and performance improvements.",
        remediation: "Upgrade ESLint to >= 8.0.0.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: null,
    },
    {
        id: "DAR015",
        packageName: "webpack",
        affectedVersionRange: "<5.0.0",
        riskLevel: "medium",
        riskCategory: "outdated_major",
        riskDescription: "Webpack versions before 5.x have known security issues and lack module federation support.",
        remediation: "Upgrade webpack to >= 5.0.0 or migrate to Vite.",
        workspaceScope: "web",
        active: true,
        introducedInPr: 75,
        cveId: null,
    },
    {
        id: "DAR016",
        packageName: "request",
        affectedVersionRange: ">=0.0.0",
        riskLevel: "high",
        riskCategory: "abandoned",
        riskDescription: "The 'request' package is deprecated and no longer maintained. No security patches will be issued.",
        remediation: "Migrate to axios, got, or node-fetch.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: null,
    },
    {
        id: "DAR017",
        packageName: "colors",
        affectedVersionRange: ">=1.4.1 <1.4.44",
        riskLevel: "critical",
        riskCategory: "security_vulnerability",
        riskDescription: "Malicious code was injected into colors 1.4.1 causing infinite loops (supply chain attack).",
        remediation: "Pin colors to 1.4.0 or migrate to chalk.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2022-0144",
    },
    {
        id: "DAR018",
        packageName: "faker",
        affectedVersionRange: ">=6.0.0-alpha.1 <6.0.0",
        riskLevel: "critical",
        riskCategory: "security_vulnerability",
        riskDescription: "Malicious code was injected into faker 6.0.0-alpha versions (supply chain attack, same author as colors).",
        remediation: "Use @faker-js/faker which is the community-maintained fork.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: null,
    },
    {
        id: "DAR019",
        packageName: "node-ipc",
        affectedVersionRange: ">=10.1.1 <10.1.3",
        riskLevel: "critical",
        riskCategory: "security_vulnerability",
        riskDescription: "node-ipc 10.1.1 and 10.1.2 contained malicious code that deleted files based on geolocation (supply chain attack).",
        remediation: "Upgrade to node-ipc >= 10.1.3 or use alternative IPC libraries.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2022-23812",
    },
    {
        id: "DAR020",
        packageName: "ua-parser-js",
        affectedVersionRange: ">=0.7.29 <0.7.30",
        riskLevel: "critical",
        riskCategory: "security_vulnerability",
        riskDescription: "ua-parser-js 0.7.29 was compromised to install cryptominer and password stealer (supply chain attack).",
        remediation: "Upgrade ua-parser-js to >= 0.7.30.",
        workspaceScope: "all",
        active: true,
        introducedInPr: 75,
        cveId: "CVE-2021-41265",
    },
]);
// ---------------------------------------------------------------------------
// Registry object
// ---------------------------------------------------------------------------
export const SMART_SHIKKA_DEPENDENCY_AUDIT_RISK_REGISTRY = deepFreeze({
    entries: SMART_SHIKKA_DEPENDENCY_AUDIT_ENTRIES,
    generatedInPr: 75,
    liveExecutionPerformed: false,
    networkCallMade: false,
    dbWriteMade: false,
});
//# sourceMappingURL=dependencyAuditRiskRegistry.js.map